import { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Severity } from "../../api/types";

interface Props {
    value: Severity;
    onSelect: (value: Severity) => void;
    disabled?: boolean;
}

const SeveritySelector = ({ value, onSelect, disabled }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const arrowRef = useRef<HTMLDivElement>(null);
    const leftLimitRef = useRef<number>(0);
    const rightLimitRef = useRef<number>(0);
    const [isDragging, setIsDragging] = useState(false);

    const level0Ref = useRef<HTMLDivElement>(null);
    const level1Ref = useRef<HTMLDivElement>(null);
    const level2Ref = useRef<HTMLDivElement>(null);
    const level3Ref = useRef<HTMLDivElement>(null);

    const severityLevels: Array<{
        label: string;
        value: Severity;
        ref: React.RefObject<HTMLDivElement>;
    }> = [
        { label: "Undetermined", value: Severity.UNDETERMINED, ref: level0Ref },
        { label: "Mild", value: Severity.MILD, ref: level1Ref },
        { label: "Moderate", value: Severity.MODERATE, ref: level2Ref },
        { label: "Severe", value: Severity.SEVERE, ref: level3Ref },
    ];

    // Initialize and update arrow position when value changes
    useEffect(() => {
        if (!containerRef.current) return;

        // Calculate drag limits
        const containerRect = containerRef.current.getBoundingClientRect();
        const firstLevel = severityLevels[0];
        const lastLevel = severityLevels[severityLevels.length - 1];

        if (firstLevel.ref.current && lastLevel.ref.current) {
            const firstRect = firstLevel.ref.current.getBoundingClientRect();
            const lastRect = lastLevel.ref.current.getBoundingClientRect();

            // Set limits to the center of first and last elements
            leftLimitRef.current =
                firstRect.left + firstRect.width / 2 - containerRect.left;
            rightLimitRef.current =
                lastRect.left + lastRect.width / 2 - containerRect.left;
        }
    }, []);

    useEffect(() => {
        updateArrowPosition(value);
    }, [value]);
    
    const updateArrowPosition = useCallback((severity: Severity) => {
        if (!arrowRef.current || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        let position = 0;

        const selectedLevel = severityLevels.find(
            (level) => level.value === severity
        );
        if (selectedLevel && selectedLevel.ref.current) {
            const levelRect = selectedLevel.ref.current.getBoundingClientRect();
            const levelPosition = levelRect.left - containerRect.left;
            const levelWidth = levelRect.width;
            position = levelPosition + levelWidth / 2;
        }

        arrowRef.current.style.left = `${position}px`;
    }, []);

    const getOptionPositions = (): {
        severity: Severity;
        left: number;
    }[] => {
        if (!containerRef.current) return [];

        const levelPositions = severityLevels.map((level) => {
            const ref = level.ref.current;
            if (!ref) return { severity: level.value, left: 0 };
            const rect = ref.getBoundingClientRect();
            const left = rect.left + rect.width / 2;
            return { severity: level.value, left: left };
        });
        return levelPositions;
    };

    const findClosestSeverity = (leftPos: number): Severity => {
        const options = getOptionPositions();
        let closest = options[0];
        let minDistance = Math.abs(leftPos - options[0].left);

        for (const option of options) {
            const distance = Math.abs(leftPos - option.left);
            if (distance < minDistance) {
                minDistance = distance;
                closest = option;
            }
        }

        return closest.severity;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (disabled) return;
        e.preventDefault();

        const initialMouseX = e.clientX;
        const initialLeft = arrowRef.current!.style.left
            ? parseFloat(arrowRef.current!.style.left)
            : 0;

        setIsDragging(true);

        const handleMouseMove = (e: MouseEvent) => {
            if (!containerRef.current) return;
            const deltaX = e.clientX - initialMouseX;
            const newPosition = initialLeft + deltaX;

            // Constrain position within limits
            const constrainedPosition = Math.max(
                leftLimitRef.current,
                Math.min(newPosition, rightLimitRef.current)
            );

            // Update drag position for smooth visual feedback
            arrowRef.current!.style.left = `${constrainedPosition}px`;
        };

        const handleMouseUp = (e: MouseEvent) => {
            // Calculate final selection based on arrow position relative to option positions
            const arrowElement = arrowRef.current;
            if (!arrowElement) return;
            const closestItem = findClosestSeverity(e.clientX);
            updateArrowPosition(closestItem);
            onSelect(closestItem);

            setIsDragging(false);
            arrowElement.style.transform = "translateX(0)"; // Reset position
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    return (
        <div className="h-fit flex items-center shrink-0 gap-1 text-white">
            <div className="xl:text-lg font-bold pe-1.5">Severity</div>

            {/* Combined draggable area */}
            <div className="flex-1 relative" ref={containerRef}>
                {/* Full draggable background */}
                <div className="flex items-center gap-1">
                    {/* Undetermined section */}
                    <div
                        className={twMerge(
                            "bg-[#CECECE] w-7 h-[3px] rounded cursor-pointer",
                            disabled && "!grayscale !cursor-not-allowed"
                        )}
                        onClick={() => onSelect(Severity.UNDETERMINED)}
                        onMouseDown={handleMouseDown}
                        ref={level0Ref}
                    />

                    {/* Severity gradient bar */}
                    <div
                        className={twMerge(
                            "h-[3px] w-52 xl:w-[230px] rounded-full relative cursor-pointer",
                            disabled && "!grayscale !cursor-not-allowed"
                        )}
                        style={{
                            backgroundImage: `linear-gradient(to right, #32F532 0%, #F5FD13 33.33%, #F3A246 66.66%, #FE6E6E 100%)`,
                        }}
                        onMouseDown={handleMouseDown}
                    />
                </div>

                {/* Single draggable SelectArrow */}
                <div
                    ref={arrowRef}
                    className={twMerge(
                        "absolute top-0 transform -translate-x-1/2",
                        isDragging ? "cursor-grabbing" : "cursor-grab"
                    )}
                    onMouseDown={handleMouseDown}
                >
                    <SelectArrow isActive={true} disabled={disabled} />
                </div>

                {/* Labels for severity levels only */}
                <div className="absolute top-0 left-8 inset-y-0 w-52 xl:w-[230px] grid grid-cols-[62px_auto_62px]">
                    {severityLevels.slice(1).map((level) => {
                        return (
                            <button
                                type="button"
                                disabled={disabled}
                                key={level.value}
                                className={`group text-center relative`}
                                onClick={() => onSelect(level.value)}
                            >
                                <div
                                    className="absolute -top-0.5 left-1/2 w-0.5 h-2 bg-[#CECECE] transform -translate-x-1/2"
                                    ref={level.ref}
                                ></div>
                                <div className="font-semibold xl:text-lg mt-2">
                                    {level.label}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Undetermined label */}
                <button
                    type="button"
                    disabled={disabled}
                    className="absolute top-0 left-0 w-7 text-center group"
                    onClick={() => onSelect(Severity.UNDETERMINED)}
                ></button>
            </div>
        </div>
    );
};

export default SeveritySelector;

const SelectArrow = ({
    isActive,
    disabled,
}: {
    isActive: boolean;
    disabled?: boolean;
}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="21"
        viewBox="0 0 25 21"
        fill="none"
        className={twMerge(
            "max-xl:h-3.5 absolute left-1/2 -translate-x-1/2 bottom-full",
            isActive
                ? "opacity-100"
                : ["opacity-0", !disabled && "group-hover:opacity-60"]
        )}
    >
        <g filter="url(#filter0_d_671_4204)">
            <path
                d="M12.5001 12.5C12.1597 12.4983 11.824 12.4208 11.5172 12.2732C11.2105 12.1256 10.9405 11.9116 10.7268 11.6467L5.11343 4.84667C4.78542 4.43729 4.57901 3.94401 4.51772 3.42302C4.45642 2.90204 4.54272 2.37432 4.76676 1.9C4.94846 1.48777 5.24499 1.13655 5.62092 0.888297C5.99685 0.640042 6.43629 0.505244 6.88676 0.5H18.1134C18.5639 0.505244 19.0033 0.640042 19.3793 0.888297C19.7552 1.13655 20.0517 1.48777 20.2334 1.9C20.4575 2.37432 20.5438 2.90204 20.4825 3.42302C20.4212 3.94401 20.2148 4.43729 19.8868 4.84667L14.2734 11.6467C14.0597 11.9116 13.7897 12.1256 13.4829 12.2732C13.1762 12.4208 12.8405 12.4983 12.5001 12.5Z"
                fill="white"
            />
        </g>
    </svg>
);
